/**
 * Nutrient registry. Calories, protein, carbs and fat are the base nutrients
 * (always tracked). Everything else is opt-in, and users can add their own.
 *
 * Values are stored as open maps keyed by `key`. A missing key means
 * "unknown", never zero — totals report how many entries had data.
 *
 * Daily values: FDA 2016 Nutrition Facts rule (adults and children >= 4).
 * Caffeine: FDA guidance of up to 400 mg/day for healthy adults.
 */

export type NutrientUnit = 'kcal' | 'g' | 'mg' | 'µg' | 'IU' | 'ml' | 'unit';
export type GoalDirection = 'min' | 'max' | 'target';
export type NutrientGroup = 'energy' | 'macros' | 'carbs' | 'fats' | 'minerals' | 'vitamins' | 'other' | 'custom';

export interface NutrientDef {
  key: string;
  name: string;
  unit: NutrientUnit;
  group: NutrientGroup;
  /** min = "at least", max = "at most", target = "aim for". */
  direction: GoalDirection;
  /** Reference daily value, when one exists. */
  dv?: number;
  decimals: number;
  base?: boolean;
  custom?: boolean;
}

/** Per-100 g (or per-entry) nutrient amounts. Missing key = unknown. */
export type NutrientMap = Record<string, number>;

/**
 * Most nutrients one map can hold (a food's, an entry's, a recipe's). The built-in
 * nutrients are 40 and a food from the catalog or FatSecret carries at most 19, so this
 * leaves room for plenty of your own while keeping what's stored bounded.
 */
export const MAX_NUTRIENT_KEYS = 100;

export const BASE_KEYS = ['kcal', 'protein', 'carbs', 'fat'] as const;
export type BaseKey = (typeof BASE_KEYS)[number];

export const BUILTIN_NUTRIENTS: readonly NutrientDef[] = [
  { key: 'kcal', name: 'Calories', unit: 'kcal', group: 'energy', direction: 'target', dv: 2000, decimals: 0, base: true },
  { key: 'protein', name: 'Protein', unit: 'g', group: 'macros', direction: 'min', dv: 50, decimals: 0, base: true },
  { key: 'carbs', name: 'Carbs', unit: 'g', group: 'macros', direction: 'target', dv: 275, decimals: 0, base: true },
  { key: 'fat', name: 'Fat', unit: 'g', group: 'macros', direction: 'target', dv: 78, decimals: 0, base: true },

  { key: 'fiber', name: 'Fiber', unit: 'g', group: 'carbs', direction: 'min', dv: 28, decimals: 0 },
  { key: 'sugar', name: 'Total sugars', unit: 'g', group: 'carbs', direction: 'max', decimals: 0 },
  { key: 'added_sugar', name: 'Added sugars', unit: 'g', group: 'carbs', direction: 'max', dv: 50, decimals: 0 },

  { key: 'sat_fat', name: 'Saturated fat', unit: 'g', group: 'fats', direction: 'max', dv: 20, decimals: 1 },
  { key: 'trans_fat', name: 'Trans fat', unit: 'g', group: 'fats', direction: 'max', decimals: 1 },
  { key: 'mono_fat', name: 'Monounsaturated fat', unit: 'g', group: 'fats', direction: 'target', decimals: 1 },
  { key: 'poly_fat', name: 'Polyunsaturated fat', unit: 'g', group: 'fats', direction: 'target', decimals: 1 },
  { key: 'omega_3', name: 'Omega-3', unit: 'g', group: 'fats', direction: 'min', decimals: 2 },
  { key: 'cholesterol', name: 'Cholesterol', unit: 'mg', group: 'fats', direction: 'max', dv: 300, decimals: 0 },

  { key: 'sodium', name: 'Sodium', unit: 'mg', group: 'minerals', direction: 'max', dv: 2300, decimals: 0 },
  { key: 'potassium', name: 'Potassium', unit: 'mg', group: 'minerals', direction: 'min', dv: 4700, decimals: 0 },
  { key: 'calcium', name: 'Calcium', unit: 'mg', group: 'minerals', direction: 'min', dv: 1300, decimals: 0 },
  { key: 'iron', name: 'Iron', unit: 'mg', group: 'minerals', direction: 'min', dv: 18, decimals: 1 },
  { key: 'magnesium', name: 'Magnesium', unit: 'mg', group: 'minerals', direction: 'min', dv: 420, decimals: 0 },
  { key: 'zinc', name: 'Zinc', unit: 'mg', group: 'minerals', direction: 'min', dv: 11, decimals: 1 },
  { key: 'phosphorus', name: 'Phosphorus', unit: 'mg', group: 'minerals', direction: 'min', dv: 1250, decimals: 0 },
  { key: 'selenium', name: 'Selenium', unit: 'µg', group: 'minerals', direction: 'min', dv: 55, decimals: 0 },
  { key: 'copper', name: 'Copper', unit: 'mg', group: 'minerals', direction: 'min', dv: 0.9, decimals: 2 },
  { key: 'manganese', name: 'Manganese', unit: 'mg', group: 'minerals', direction: 'min', dv: 2.3, decimals: 1 },
  { key: 'iodine', name: 'Iodine', unit: 'µg', group: 'minerals', direction: 'min', dv: 150, decimals: 0 },

  { key: 'vitamin_a', name: 'Vitamin A', unit: 'µg', group: 'vitamins', direction: 'min', dv: 900, decimals: 0 },
  { key: 'vitamin_c', name: 'Vitamin C', unit: 'mg', group: 'vitamins', direction: 'min', dv: 90, decimals: 0 },
  { key: 'vitamin_d', name: 'Vitamin D', unit: 'µg', group: 'vitamins', direction: 'min', dv: 20, decimals: 1 },
  { key: 'vitamin_e', name: 'Vitamin E', unit: 'mg', group: 'vitamins', direction: 'min', dv: 15, decimals: 1 },
  { key: 'vitamin_k', name: 'Vitamin K', unit: 'µg', group: 'vitamins', direction: 'min', dv: 120, decimals: 0 },
  { key: 'thiamin', name: 'Thiamin (B1)', unit: 'mg', group: 'vitamins', direction: 'min', dv: 1.2, decimals: 2 },
  { key: 'riboflavin', name: 'Riboflavin (B2)', unit: 'mg', group: 'vitamins', direction: 'min', dv: 1.3, decimals: 2 },
  { key: 'niacin', name: 'Niacin (B3)', unit: 'mg', group: 'vitamins', direction: 'min', dv: 16, decimals: 1 },
  { key: 'vitamin_b6', name: 'Vitamin B6', unit: 'mg', group: 'vitamins', direction: 'min', dv: 1.7, decimals: 2 },
  { key: 'folate', name: 'Folate', unit: 'µg', group: 'vitamins', direction: 'min', dv: 400, decimals: 0 },
  { key: 'vitamin_b12', name: 'Vitamin B12', unit: 'µg', group: 'vitamins', direction: 'min', dv: 2.4, decimals: 1 },
  { key: 'biotin', name: 'Biotin', unit: 'µg', group: 'vitamins', direction: 'min', dv: 30, decimals: 0 },
  { key: 'pantothenic_acid', name: 'Pantothenic acid', unit: 'mg', group: 'vitamins', direction: 'min', dv: 5, decimals: 1 },
  { key: 'choline', name: 'Choline', unit: 'mg', group: 'vitamins', direction: 'min', dv: 550, decimals: 0 },

  { key: 'caffeine', name: 'Caffeine', unit: 'mg', group: 'other', direction: 'max', dv: 400, decimals: 0 },
  { key: 'alcohol', name: 'Alcohol', unit: 'g', group: 'other', direction: 'max', decimals: 0 },
];

export const BUILTIN_BY_KEY: ReadonlyMap<string, NutrientDef> = new Map(BUILTIN_NUTRIENTS.map((n) => [n.key, n]));

export const GROUP_LABELS: Record<NutrientGroup, string> = {
  energy: 'Energy',
  macros: 'Macros',
  carbs: 'Carb details',
  fats: 'Fat details',
  minerals: 'Minerals',
  vitamins: 'Vitamins',
  other: 'Other',
  custom: 'Your nutrients',
};

export const CUSTOM_PREFIX = 'custom_';

export function isBaseKey(key: string): key is BaseKey {
  return (BASE_KEYS as readonly string[]).includes(key);
}

export function customKeyFromName(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return `${CUSTOM_PREFIX}${slug || 'nutrient'}`;
}

/** Scale a per-100 g map to a number of grams. Unknown keys stay unknown. */
export function scaleMap(per100: NutrientMap, grams: number): NutrientMap {
  const k = grams / 100;
  const out: NutrientMap = {};
  for (const [key, value] of Object.entries(per100)) {
    if (Number.isFinite(value)) out[key] = value * k;
  }
  return out;
}

/** Convert a per-serving map (e.g. a nutrition label) to per-100 g. */
export function perServingToPer100(perServing: NutrientMap, servingGrams: number): NutrientMap {
  if (!(servingGrams > 0)) return {};
  return scaleMap(perServing, (100 / servingGrams) * 100);
}

export interface TotalsWithCoverage {
  totals: NutrientMap;
  /** How many of the summed items had a value for each key. */
  known: Record<string, number>;
  count: number;
}

export function sumWithCoverage(items: NutrientMap[]): TotalsWithCoverage {
  const totals: NutrientMap = {};
  const known: Record<string, number> = {};
  for (const item of items) {
    for (const [key, value] of Object.entries(item)) {
      if (!Number.isFinite(value)) continue;
      // Own keys only: a key like "constructor" mustn't start from Object's built-in.
      totals[key] = (Object.hasOwn(totals, key) ? totals[key]! : 0) + value;
      known[key] = (Object.hasOwn(known, key) ? known[key]! : 0) + 1;
    }
  }
  return { totals, known, count: items.length };
}

/** How many decimals an amount is shown with: whole numbers from 100 up, otherwise the nutrient's own. */
export function shownDecimals(value: number, def: Pick<NutrientDef, 'decimals'>): number {
  return Math.abs(value) >= 100 ? 0 : def.decimals;
}

export function formatAmount(value: number | undefined, def: Pick<NutrientDef, 'decimals'>): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  const decimals = shownDecimals(value, def);
  return value.toLocaleString('en-US', { maximumFractionDigits: decimals, minimumFractionDigits: 0 });
}
